import { useLazyQuery } from '@apollo/client';
import { Rights } from '@dokucoach/common-types';
import ArrowButton from 'components/ArrowButton';
import { useContextAuthManager } from 'components/AuthManager';
import DropdownWithSearch from 'components/DropdownWithSearch';
import { DEFAULT_GROUP_ID } from 'components/Layout/components/DashboardAside';
import { getIcons } from 'components/ResidentHeader';
import {
  BoxAvatar,
  BoxAvatarImg,
  BoxContainerStyle,
  BoxInfo,
} from 'components/ResidentHeader/styled-components';
import { useContextSnackbar } from 'components/Snackbar/context';
import TooltipAction from 'components/Tooltip';
import { BaseDataList } from 'constants/base-data-list';
import routePaths from 'constants/route-paths';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { DoctorListData, DoctorPropType } from 'services/doctors';
import { EmployeeListData } from 'services/employees';
import { GET_RESIDENT_HEADER_FLAGS } from 'services/residenHead';
import { ResidentPropType } from 'services/residentGroups';
import styled from 'styled-components';
import { AvatarImageType, getAvatarImage } from 'utils/getAvatarImage';
import { getPath } from 'utils/path';
import { useContextLayout } from '../Layout/context';
import UploadModal from './UploadModal';

const LightP = styled.div`
  color: ${(props) => props.theme.colors.ghost};
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  grid-gap: 10px;
  @media (max-width: 414px) {
    font-size: 12px;
    flex-wrap: wrap;
  }
`;

export const LightPClickable = styled(LightP)`
  cursor: pointer;
  text-decoration: underline;
`;

export type BaseDataHeaderListItem = ResidentPropType | EmployeeListData | DoctorListData;

const BaseDataHeader = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const { hasPermission } = useContextAuthManager();
  const [currentEntity, setCurrentEntity] = useState<BaseDataHeaderListItem | undefined>();
  const [dropdownOptions, setDropdownOptions] = useState<{ text: string; value: string }[]>([]);
  const [profilePictureModalShow, setProfilePictureModalShow] = useState(false);
  const { shouldRefetchHeaderFlags, setShouldRefetchHeaderFlags } = useContextLayout();
  const {
    filteredEmployees: employees,
    filteredResidents: residents,
    filteredDoctors: doctors,
    baseDataListSelected,
    setShouldRefetch,
  } = useContextLayout();
  let routePath = '';
  const [listItems, setListItems] = useState<BaseDataHeaderListItem[]>([]);
  let description = '';
  let icons: string[] = [];

  const [
    getResidentHeaderFlagData,
    {
      data: residentHeaderFlagData,
      error: residentHeaderFlagError,
      loading: residentHeaderFlagLoading,
      refetch: refetchResidentHeaderFlags,
    },
  ] = useLazyQuery(GET_RESIDENT_HEADER_FLAGS);

  switch (baseDataListSelected) {
    case BaseDataList.EMPLOYEES:
      routePath = routePaths.DATA_EMPLOYEE;
      description = (currentEntity as EmployeeListData)?.user?.role?.name || t('noRoleAssigned');
      icons = [];
      break;
    case BaseDataList.RESIDENTS:
      routePath = routePaths.DATA_RESIDENT;
      description = `${
        (currentEntity as ResidentPropType)?.bed?.label
          ? (currentEntity as ResidentPropType)?.bed?.label
          : t('no_bed_set')
      }, `;
      if (residentHeaderFlagData) {
        const { getResidentHeaderFlags, getResident } = residentHeaderFlagData;
        const headerFlags = Object.assign(getResidentHeaderFlags, getResident);
        icons = getIcons(headerFlags);
      } else {
        icons = [];
      }
      break;
    case BaseDataList.DOCTORS:
      routePath = routePaths.DATA_DOCTOR;
      description = (currentEntity as DoctorPropType)?.subjectArea || t('noSubjectAreaSet');
      icons = [];
      break;
  }

  const path = matchPath(location.pathname, {
    path: routePath,
  }) as { params: { entityId: string } };

  const params = useMemo<{ entityId?: string }>(() => {
    return {
      entityId: path?.params?.entityId,
    };
  }, [path]);

  const getDestination = (url: string): string => {
    const splittedUrl = url.split('/');
    return splittedUrl[splittedUrl.length - 1] === 'inactive'
      ? `/${splittedUrl[splittedUrl.length - 2]}/${splittedUrl[splittedUrl.length - 1]}`
      : `/${splittedUrl[splittedUrl.length - 1]}`;
  };

  const onSelect = (entityId: string) => {
    history.push(getPath([routePath, getDestination(history.location.pathname)], { entityId }));
  };

  const onPrevious = () => {
    const index = listItems.findIndex((item) => item.id === params.entityId);
    const entityId = index <= 0 ? listItems[listItems.length - 1].id : listItems[index - 1].id;
    history.push(
      getPath([routePath, getDestination(history.location.pathname)], {
        entityId,
      }),
    );
  };

  const onNext = () => {
    const index = listItems.findIndex((item) => item.id === params.entityId);
    const entityId =
      index < 0 || index === listItems.length - 1 ? listItems[0].id : listItems[index + 1].id;
    history.push(
      getPath([routePath, getDestination(history.location.pathname)], {
        entityId,
      }),
    );
  };

  // Switch to resident care documentation menu
  const onChangeCareDocumentation = async () => {
    if (currentEntity) {
      const fromGroupId = new URLSearchParams(location.search).get('from_group');

      history.push(
        getPath([routePaths.GROUP, routePaths.RESIDENT], {
          groupId: fromGroupId || DEFAULT_GROUP_ID,
          residentId: currentEntity.id,
        }),
      );
    } else {
      console.error(`No entity id found to navigate to groups`);
    }
  };

  useEffect(() => {
    if (shouldRefetchHeaderFlags && refetchResidentHeaderFlags) {
      const refetch = async () => {
        await refetchResidentHeaderFlags();
        setShouldRefetchHeaderFlags(undefined);
      };
      refetch();
    }
  }, [shouldRefetchHeaderFlags, setShouldRefetchHeaderFlags]);

  useEffect(() => {
    if (residentHeaderFlagError) {
      showApiError(residentHeaderFlagError);
      return;
    }
  }, [residentHeaderFlagData, residentHeaderFlagError]);

  useEffect(() => {
    if (currentEntity && baseDataListSelected === BaseDataList.RESIDENTS) {
      getResidentHeaderFlagData({ variables: { residentId: currentEntity.id } });
    }
  }, [currentEntity]);

  useEffect(() => {
    switch (baseDataListSelected) {
      case BaseDataList.EMPLOYEES:
        setListItems(employees);
        break;
      case BaseDataList.RESIDENTS:
        setListItems(residents);
        break;
      case BaseDataList.DOCTORS:
        setListItems(doctors);
        break;
    }
  }, [baseDataListSelected, employees, residents, doctors]);

  useEffect(() => {
    if (params.entityId && listItems.length) {
      const entity = listItems.find((e) => e.id === params.entityId);
      setCurrentEntity(entity);
    } else {
      setCurrentEntity(undefined);
    }
  }, [listItems, path, params]);

  useEffect(() => {
    setDropdownOptions(
      listItems.length
        ? listItems.map((e) => ({
            text: `${e.name.last}, ${e.name.title ? e.name.title + ' ' : ''}${e.name.firstFull}`,
            value: e.id,
          }))
        : [],
    );
  }, [listItems]);

  const hasProfilePicture = [BaseDataList.RESIDENTS, BaseDataList.EMPLOYEES].includes(
    baseDataListSelected,
  );
  return path?.params.entityId && currentEntity ? (
    <BoxContainerStyle
      sx={{
        display: 'flex',
      }}
    >
      <BoxInfo
        sx={{
          flexGrow: 1,
        }}
      >
        {dropdownOptions.length && (
          <div className="d-flex align-items-center" id="baseDataFullName">
            <ArrowButton direction="up" onClick={onPrevious} margin="0 0.5rem 0 0" />
            <ArrowButton direction="down" onClick={onNext} margin="0 0.5rem 0 0" />
            <DropdownWithSearch
              options={dropdownOptions}
              onSelect={onSelect}
              initialValue={currentEntity.id}
            />
            {baseDataListSelected === BaseDataList.RESIDENTS && hasPermission(Rights.CARE.READ) && (
              <LightPClickable
                id="baseDataswitchNursingDocumentation"
                onClick={onChangeCareDocumentation}
              >
                {t('resident_header_change_to_care_documentation')}
              </LightPClickable>
            )}
          </div>
        )}
        <LightP>
          {baseDataListSelected === BaseDataList.EMPLOYEES &&
            ((currentEntity as EmployeeListData)?.identifier || t('noIdentifierSet'))}
          {baseDataListSelected === BaseDataList.RESIDENTS &&
            ((residentHeaderFlagData?.getResidentHeaderFlags?.careLevel &&
              currentEntity.id &&
              `${t('resident_contract_careLevelsTitle')} ${
                residentHeaderFlagData.getResidentHeaderFlags.careLevel
              }`) ||
              (!residentHeaderFlagLoading && t('resident_header_no_care_level_set')))}
          {icons.length
            ? icons
                .filter((icon: string) => icon !== 'absent')
                .map((icon) => <TooltipAction variant={icon} key={icon} />)
            : null}
        </LightP>
        <LightP id="baseDataAccomodationInfo">
          {description}
          {baseDataListSelected === BaseDataList.RESIDENTS && icons.includes('absent') && (
            <TooltipAction variant={'absent'} />
          )}
        </LightP>
      </BoxInfo>
      <BoxAvatar
        className="d-none d-md-block"
        style={hasProfilePicture ? { cursor: 'pointer' } : {}}
        onClick={hasProfilePicture ? () => setProfilePictureModalShow(true) : undefined}
      >
        {'profilePictureUrl' in currentEntity && currentEntity.profilePictureUrl ? (
          <BoxAvatarImg src={currentEntity.profilePictureUrl ?? undefined} />
        ) : (
          <SVG
            src={getAvatarImage(
              currentEntity.name.gender,
              baseDataListSelected === BaseDataList.RESIDENTS
                ? AvatarImageType.OLD
                : AvatarImageType.DEFAULT,
            )}
          />
        )}
      </BoxAvatar>
      <UploadModal
        show={currentEntity !== undefined && profilePictureModalShow}
        onClose={() => {
          setProfilePictureModalShow(false);
          setShouldRefetch(currentEntity.id);
        }}
        currentEntity={currentEntity}
        entityId={currentEntity.id}
        profilePictureUrl={
          'profilePictureUrl' in currentEntity ? currentEntity.profilePictureUrl : undefined
        }
      />
    </BoxContainerStyle>
  ) : null;
};

export default BaseDataHeader;
